import { Button, Checkbox, Col, Divider, Flex, Form, Row, Typography, Grid, Select } from 'antd';
import { FormInstance } from 'antd/es/form';
import { useTranslation } from 'react-i18next';
import { Fragment } from 'react/jsx-runtime';

import ListButton from 'components/listButton/listButton';
import Icon from 'utils/icon';
import TextArea from 'antd/es/input/TextArea';
import FormNumberElement from 'components/formNumberElement/formNumberElement';
import InfoTooltip from 'components/info/info';
import { checkArabicValidation } from 'utils/validators/arabic';
import { checkEnglishValidation } from 'utils/validators/english';
import { isRTL } from 'core/services/language';
import SelectElement from 'components/selectElement/selectElement';
import { useEffect, useState } from 'react';
import NotificationService from 'core/services/notification';
import { useDebounce } from 'use-debounce';
import { onSearchUser } from '../services/assembliesServices';
import { LoadingOutlined } from '@ant-design/icons';
import StatusComponent from 'components/status/status';
import { Decision } from '../models/assemblies';

const { useBreakpoint } = Grid;

interface AssemblyPointsProps {
	form: FormInstance;
	decisions?: Decision[];
}

function AssemblyPointsElement({ form, decisions }: AssemblyPointsProps) {
	const { t } = useTranslation();
	const screens = useBreakpoint();

	const [searchedQuery, setSearchedQuery] = useState<string>('');
	const [debouncedQuery] = useDebounce(searchedQuery, 500);

	const [searchedUsersOptions, setSearchedUsersOptions] = useState<{ label: React.ReactNode; value: string }[]>([]);
	const [isSearching, setIsSearching] = useState<boolean>(false);

	// Store all selected shareholders with their full info (for displaying as tags)
	const [allShareholdersMap, setAllShareholdersMap] = useState<Map<string, { label: React.ReactNode; value: string }>>(new Map());

	// Watch all decisions to trigger re-renders when abstention_type changes
	const watchedDecisions = Form.useWatch('decisions', form);

	// Initialize shareholders map from decisions prop
	useEffect(() => {
		if (decisions && decisions.length > 0) {
			const shareholdersMap = new Map<string, { label: React.ReactNode; value: string }>();

			decisions.forEach((decision, decisionIndex) => {
				if (decision.abstention_type === 'specific_shareholders' && decision.abstaining_shareholders) {
					const shareholderIds: string[] = [];

					decision.abstaining_shareholders.forEach((shareholder) => {
						const shareholderId = `${shareholder.id}`;
						shareholderIds.push(shareholderId);

						// Add to map if not already present
						if (!shareholdersMap.has(shareholderId)) {
							shareholdersMap.set(shareholderId, {
								label: (
									<p className="mb-0" style={{ fontSize: 15 }}>
										{`${isRTL() ? shareholder.name : (shareholder.name_en ?? shareholder.name)}`}{' '}
										<StatusComponent
											small
											color={shareholder.is_board_member ? 'green' : 'blue'}
											text={shareholder.is_board_member ? t('ACTIONS.BOARD_MEMBER') : t('COMMON.SHAREHOLDER')}
										/>
									</p>
								),
								value: shareholderId,
							});
						}
					});

					// Set the form field value with the IDs
					form.setFieldValue(['decisions', decisionIndex, 'abstaining_shareholder_ids'], shareholderIds);
				}
			});

			setAllShareholdersMap(shareholdersMap);
		}
	}, [decisions]);

	// Handle search with debounce
	useEffect(() => {
		if (debouncedQuery.length >= 3) {
			searchUserHandler(debouncedQuery);
		} else {
			setSearchedUsersOptions([]);
		}
	}, [debouncedQuery]);

	const validateArabic = (_: any, value: string) => {
		if (!value) return Promise.resolve();
		if (checkArabicValidation(_, value)) {
			return Promise.resolve();
		} else if (value) return Promise.reject(new Error(t('COMMON.ARABIC_ONLY')));
		else return Promise.reject();
	};

	const validateEnglish = (_: any, value: string) => {
		if (!value) return Promise.resolve();
		if (checkEnglishValidation(_, value)) {
			return Promise.resolve();
		} else if (value) return Promise.reject(new Error(t('COMMON.ENGLISH_ONLY')));
		else return Promise.reject();
	};

	const searchUserHandler = async (text: string) => {
		try {
			setIsSearching(true);

			const response = await onSearchUser(text);

			const newOptions = response.map((user) => ({
				label: (
					<p className="mb-0" style={{ fontSize: 15 }}>
						{`${isRTL() ? user.name : (user.name_en ?? user.name)}`}{' '}
						<StatusComponent
							small
							color={user.is_board_member ? 'green' : 'blue'}
							text={user.is_board_member ? t('ACTIONS.BOARD_MEMBER') : t('COMMON.SHAREHOLDER')}
						/>
					</p>
				),
				value: `${user.shareholder_id}`,
			}));

			setSearchedUsersOptions(newOptions);
			setIsSearching(false);
		} catch (error) {
			setIsSearching(false);
			NotificationService.error(t('COMMON.ERROR'), t('COMMON.FETCH_ERROR'));
		}
	};

	const handleSelectChange = (values: string[], name: number) => {
		// Update the form field
		form.setFieldValue(['decisions', name, 'abstaining_shareholder_ids'], values);

		// Add newly selected options to the map in a single setState call
		const newMap = new Map(allShareholdersMap);
		let hasChanges = false;

		searchedUsersOptions.forEach((option) => {
			if (values.includes(option.value) && !newMap.has(option.value)) {
				newMap.set(option.value, option);
				hasChanges = true;
			}
		});

		if (hasChanges) {
			setAllShareholdersMap(newMap);
		}

		// Clear search
		setSearchedQuery('');
		setSearchedUsersOptions([]);
	};

	// Get options for a specific decision (combines selected shareholders + search results)
	const getOptionsForDecision = (decisionIndex: number) => {
		const selectedIds = form.getFieldValue(['decisions', decisionIndex, 'abstaining_shareholder_ids']) || [];

		// Get selected options from the map
		const selectedOptions = selectedIds.map((id: string) => allShareholdersMap.get(id)).filter(Boolean);

		// Combine with search results, avoiding duplicates
		const combined = [...selectedOptions];
		searchedUsersOptions.forEach((option) => {
			if (!combined.some((existing) => existing.value === option.value)) {
				combined.push(option);
			}
		});

		return combined;
	};

	return (
		<>
			<h6 className="mb-3" style={{ fontSize: '1.1rem' }}>
				{t('ACTIONS.VOTING_ITEMS')}
			</h6>
			<Row gutter={[16, 16]}>
				<Form.List name="decisions">
					{(fields, { add, remove }) => (
						<>
							{fields.map(({ key, name, ...restField }, index) => {
								return (
									<Fragment key={key}>
										<Col span={24} md={12} lg={8}>
											<Typography style={{ fontSize: 15, marginBottom: 5 }}>{t('ACTIONS.POINT_IN_AR')}</Typography>
											<Form.Item
												{...restField}
												name={[name, 'translated_title', 'ar']}
												style={{ marginBottom: 0, maxWidth: 'unset' }}
												rules={[{ required: isRTL(), message: t('COMMON.FIELD_REQUIRED') }, { validator: validateArabic }]}>
												<TextArea autoSize={{ minRows: 4, maxRows: 4 }} />
											</Form.Item>
										</Col>

										<Col span={24} md={12} lg={8}>
											<Typography style={{ fontSize: 15, marginBottom: 5 }}>{t('ACTIONS.POINT_IN_EN')}</Typography>
											<Form.Item
												{...restField}
												name={[name, 'translated_title', 'en']}
												style={{ marginBottom: 0, maxWidth: 'unset' }}
												rules={[{ required: !isRTL(), message: t('COMMON.FIELD_REQUIRED') }, { validator: validateEnglish }]}>
												<TextArea autoSize={{ minRows: 4, maxRows: 4 }} />
											</Form.Item>
										</Col>

										<Col span={24} md={24} lg={8}>
											<Flex justify="space-between" gap={20} className="h-100" style={{ paddingInlineEnd: 10 }}>
												<Flex
													vertical={screens.lg}
													gap={10}
													style={{
														flex: 1,
														flexDirection: screens.lg ? 'column' : 'row',
														alignItems: screens.lg ? 'flex-start' : 'center',
													}}
													className="h-100">
													<FormNumberElement
														{...restField}
														name={[name, 'quorum_percentage']}
														label={t('ACTIONS.QUORUM_PERCENTAGE')}
														min={0}
														max={100}
														precision={2}
														addonAfter={<span className="px-3 fw-semibold fw-se">%</span>}
														rules={[{ required: true, message: t('COMMON.FIELD_REQUIRED') }]}
													/>
													<>
														<Checkbox
															onChange={(e) => form.setFieldValue(['decisions', name, 'type'], e.target.checked ? 'ShareholderCount' : 'ShareCount')}
															defaultChecked={form.getFieldValue(['decisions', name, 'type']) == 'ShareholderCount'}
															style={{ marginTop: screens.lg ? 0 : 15 }}>
															{t('ACTIONS.SHAREHOLDER_COUNT')}
															<InfoTooltip title={t('ACTIONS.EACH_SHAREHOLDER_REPRESENT_ONE_VOTE')} />
														</Checkbox>
													</>
												</Flex>
												<div style={{ marginTop: screens.lg ? 28 : 25 }}>
													<ListButton type="delete" onClick={() => remove(name)} />
												</div>
											</Flex>
										</Col>

										<Col span={24}>
											<Row gutter={[16, 16]}>
												<Col span={24} md={8}>
													<SelectElement
														{...restField}
														name={[name, 'abstention_type']}
														label={t('ACTIONS.ABSTENTION_TYPE')}
														fullWidth
														rules={[{ required: true, message: t('COMMON.FIELD_REQUIRED') }]}
														options={[
															{ label: t('ACTIONS.NONE'), value: 'none' },
															{ label: t('ACTIONS.ALL_BOARD_MEMBERS'), value: 'all_board_members' },
															{ label: t('ACTIONS.SPECIFIC_SHAREHOLDERS'), value: 'specific_shareholders' },
														]}
													/>
												</Col>

												{watchedDecisions?.[name]?.abstention_type === 'specific_shareholders' && (
													<Col span={24} md={15}>
														<Form.Item
															{...restField}
															name={[name, 'abstaining_shareholder_ids']}
															style={{ maxWidth: 'unset' }}
															rules={[{ required: true, message: t('COMMON.FIELD_REQUIRED') }]}>
															<Select
																mode="multiple"
																style={{ width: '100%', marginTop: 28 }}
																placeholder={t('ACTIONS.SEARCH_BY_NATIONAL_ID_OR_NAME')}
																suffixIcon={<Icon name="search" />}
																options={getOptionsForDecision(name)}
																onChange={(values) => handleSelectChange(values, name)}
																onSearch={setSearchedQuery}
																searchValue={searchedQuery}
																loading={isSearching}
																showSearch
																filterOption={false}
																notFoundContent={
																	isSearching ? (
																		<span>
																			<LoadingOutlined style={{ marginInlineEnd: 10, color: 'var(--primary)' }} /> {t('COMMON.SEARCHING')}
																		</span>
																	) : (
																		t('COMMON.NO_DATA_FOUND')
																	)
																}
															/>
														</Form.Item>
													</Col>
												)}
											</Row>
										</Col>

										{fields.length !== index + 1 && <Divider className="my-1" />}
									</Fragment>
								);
							})}

							<Button type="link" className="p-0" onClick={() => add({ type: 'ShareCount', abstention_type: 'none' })}>
								<Icon name="plus" width={13} height={13} color="var(--primary)" />
								{t('ACTIONS.ADD_ANOTHER_POINT')}
							</Button>
						</>
					)}
				</Form.List>
			</Row>
		</>
	);
}

export default AssemblyPointsElement;
